import {
  Button,
  Flex,
  TextField,
  SelectField,
  CheckboxField,
  Label,
  Text,
  PhoneNumberField,
} from "@aws-amplify/ui-react";
import { DatePicker } from "rsuite";

import { useForm, Controller } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { postMinorDependentSchema } from "./validations/postValidation";
import { zodResolver } from "@hookform/resolvers/zod";
import FlexFormContainer from "../common/FlexFormContainer";
import { useMinorDependentPostMutation } from "./api/useMinorDependentsMutation";
import { useUserQuery } from "../common/useUserQuery";
import { useEffect } from "react";

export default function MinorDependentsAddPage() {
  const { data: userQueryData } = useUserQuery();
  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    getValues,
  } = useForm({ resolver: zodResolver(postMinorDependentSchema) });
  const navigate = useNavigate();
  const {
    mutate: minorDependentPostMutation,
    error,
    isLoading,
    isSuccess,
  } = useMinorDependentPostMutation();

  useEffect(() => {
    if (isSuccess) {
      navigate("/user/hr/switch-profile");
    }
  }, [isSuccess, navigate]);

  const onSubmit = (formValues) => {
    const phoneFormat = !formValues.primaryPhone
      ? null
      : `${formValues.dialCode}-${formValues.primaryPhone}`;
    minorDependentPostMutation({
      ...formValues,
      dob: formValues?.dob?.toISOString()?.split("T")[0],
      caregiverUserId: userQueryData?.patientUserId,
      primaryPhone: phoneFormat,
    });
  };

  if (isLoading) return "Saving...";

  if (error)
    return "An error ocurred while saving your changes. Please refresh the page and try again!";

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <FlexFormContainer headerTitle={"Add Minor Dependent"}>
        <Flex direction="column" alignItems="flex-start">
          <Controller
            name="givenName"
            control={control}
            render={({ field }) => (
              <TextField
                label="*Given name"
                {...field}
                value={field.value || ""}
                errorMessage={errors.givenName?.message}
                hasError={!!errors.givenName}
              />
            )}
          />
          <Controller
            name="dob"
            control={control}
            render={({ field }) => (
              <Flex direction="column" gap={9}>
                <Label htmlFor="dob">*Date of birth</Label>
                <Flex>
                  <DatePicker
                    id={field.name}
                    value={field.value}
                    onChange={(value) => field.onChange(value)}
                    format="yyyy-MM-dd"
                    oneTap
                  />
                </Flex>

                {!!errors.dob && (
                  <Text
                    variation="error"
                    as="p"
                    lineHeight="0.5em"
                    fontWeight={400}
                    fontSize="0.8em"
                    fontStyle="normal"
                    textDecoration="none"
                    width="30vw"
                    style={{ marginLeft: "10px" }}
                  >
                    {errors.dob.message}
                  </Text>
                )}
              </Flex>
            )}
          />
          <Controller
            name="gender"
            control={control}
            render={({ field }) => (
              <SelectField
                minWidth="300px"
                label="*Sex"
                placeholder="Select"
                {...field}
                value={field.value || ""}
                errorMessage={errors.gender?.message}
                hasError={!!errors.gender}
              >
                <option value="FEMALE">Female</option>
                <option value="MALE">Male</option>
              </SelectField>
            )}
          />

          <Flex
            gap="24px"
            direction={{ base: "column", medium: "row" }}
            justifyContent="flex-start"
            alignItems="flex-start"
          >
            <Controller
              name="address1"
              control={control}
              render={({ field }) => (
                <TextField
                  minWidth="300px"
                  label="*Address line 1"
                  {...field}
                  value={field.value || ""}
                  errorMessage={errors.address1?.message}
                  hasError={!!errors.address1}
                />
              )}
            />
            <Controller
              name="address2"
              control={control}
              render={({ field }) => (
                <TextField
                  minWidth="300px"
                  label="Address line 2"
                  {...field}
                  value={field.value || ""}
                  errorMessage={errors.address2?.message}
                  hasError={!!errors.address2}
                />
              )}
            />
          </Flex>
          <Flex
            gap="24px"
            direction={{ base: "column", medium: "row" }}
            justifyContent="flex-start"
            alignItems="flex-start"
          >
            <Controller
              name="city"
              control={control}
              render={({ field }) => (
                <TextField
                  minWidth="300px"
                  label="*City"
                  {...field}
                  value={field.value || ""}
                  errorMessage={errors.city?.message}
                  hasError={!!errors.city}
                />
              )}
            />
            <Controller
              name="state"
              control={control}
              render={({ field }) => (
                <TextField
                  minWidth="300px"
                  label="*State"
                  {...field}
                  value={field.value || ""}
                  errorMessage={errors.state?.message}
                  hasError={!!errors.state}
                />
              )}
            />
          </Flex>
          <Controller
            name="zip"
            control={control}
            render={({ field }) => (
              <TextField
                minWidth="300px"
                label="*Zip code"
                {...field}
                value={field.value || ""}
                errorMessage={errors.zip?.message}
                hasError={!!errors.zip}
              />
            )}
          />
          <Controller
            name="primaryPhone"
            control={control}
            render={({ field }) => (
              <PhoneNumberField
                {...field}
                label="*Primary Phone"
                defaultDialCode="+1"
                dialCodeName="dialCode"
                dialCodeLabel="Dial Code"
                errorMessage={errors.primaryPhone?.message}
                hasError={!!errors.primaryPhone}
                onChange={(e) => {
                  const dialCode = getValues("dialCode");
                  if (!dialCode) {
                    setValue("dialCode", "+1");
                  }
                  field.onChange(e.target.value);
                }}
                onDialCodeChange={(e) => {
                  setValue("dialCode", e.target.value);
                }}
              />
            )}
          />
          <Controller
            name="minorRelationship"
            control={control}
            render={({ field }) => (
              <SelectField
                minWidth="300px"
                label="*Minor relationship"
                placeholder="Select"
                {...field}
                value={field.value || ""}
                errorMessage={errors.minorRelationship?.message}
                hasError={!!errors.minorRelationship}
              >
                <option value="SON">Son</option>
                <option value="DAUGHTER">Daughter</option>
                <option value="STEPCHILD">Stepchild</option>
                <option value="ELIGIBLE_FOSTER_CHILD">
                  Eligible foster child
                </option>
                <option value="BROTHER">Brother</option>
                <option value="SISTER">Sister</option>
                <option value="HALF_SISTER_OR_BROTHER">
                  Half-sister or -brother
                </option>
                <option value="STEPBROTHER">Stepbrother</option>
                <option value="STEPSISTER">Stepsister</option>
                <option value="ADOPTED_CHILD">Adopted child</option>
              </SelectField>
            )}
          />
          <Controller
            name="certifiedLegalRight"
            control={control}
            render={({ field }) => (
              <CheckboxField
                label="*I certified that I have the legal right to this minor's medical information."
                {...field}
                value={field.value || ""}
                errorMessage={errors.certifiedLegalRight?.message}
                hasError={!!errors.certifiedLegalRight}
              />
            )}
          />
          <Controller
            name="confirmReadAuthorization"
            control={control}
            render={({ field }) => (
              <CheckboxField
                label="*I confirm that I have read and considered the contents of this authorization and agree to be bound by them."
                {...field}
                value={field.value || ""}
                errorMessage={errors.confirmReadAuthorization?.message}
                hasError={!!errors.confirmReadAuthorization}
              />
            )}
          />
        </Flex>
        <Flex gap="18px" direction="column" alignItems="flex-start">
          <Button variation="primary" children="Save" type="submit" />
        </Flex>
      </FlexFormContainer>
    </form>
  );
}
